'use client'

import { useLatest } from '@/core/hooks/use-latest'
import { useEffect, useRef, useState } from 'react'
import { LuShieldCheck } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'
import { toast } from '@/core/lib/toast'

import {
  Foot,
  ModalDesc,
  ModalTitle,
  TextLink,
} from '@/features/veluvit/components/overlay/layer-parts'
import {
  identityMessageSchema,
  identityResultSchema,
  identityStartSchema,
} from '@/features/veluvit/schemas/identity.schema'
import { refreshAdult } from '@/features/veluvit/services/adult.service'
import { api, ApiError } from '@/features/veluvit/services/api'
import {
  clearIdentityAttempt,
  identityChannelName,
  rememberIdentityAttempt,
} from '@/features/veluvit/services/identity-return'
import {
  ensureAccount,
  refreshSession,
} from '@/features/veluvit/services/session.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { AdultReason } from '@/features/veluvit/types/api.types'
import type { VerificationStatus } from '@/features/veluvit/schemas/identity.schema'

/** 인증 상태 조회 간격 — 백엔드 정의서: 2초 이상 */
const POLL_MS = 2500
/** 인증 시도 유효 시간(서버 10분) 보다 조금 길게 기다린 뒤 멈춘다 */
const GIVE_UP_MS = 11 * 60 * 1000

type Attempt = {
  id: string
  popup: Window
  startedAt: number
  identity: number
  userId: string
  channel?: BroadcastChannel
  verified?: boolean
}

/** 서버가 성인모드를 막은 이유 → 안내 */
const REASON_TEXT: Record<AdultReason, string> = {
  authentication_required: '먼저 시작하기를 눌러 계정을 만들어 주세요.',
  identity_required: '휴대폰 본인확인을 한 번 마치면 이용할 수 있어요.',
  underage: '성인모드는 만 19세 이상만 이용할 수 있어요.',
  identity_expired: '본인확인 유효기간이 지났어요. 다시 확인해 주세요.',
  invalid_identity: '본인확인 정보를 확인하지 못했어요. 다시 인증해 주세요.',
}
const STATUS_TEXT: Partial<Record<VerificationStatus, string>> = {
  failed: '서비스에서 본인확인을 완료하지 못했어요.',
  cancelled: '본인확인을 취소했어요.',
  expired: '인증 시간이 지났어요. 다시 시도해 주세요.',
}

/** 상태와 공개 오류 코드를 보존한다. 공급자 원문 결과는 화면에 전달하지 않는다. */
function resultOf(value: unknown, id: string) {
  const parsed = identityResultSchema.safeParse(value)
  if (!parsed.success) return null
  const result =
    'verification' in parsed.data ? parsed.data.verification : parsed.data
  if (result.id && result.id !== id) return null
  return result
}

/**
 * 성인 인증 — 다날 휴대폰 본인확인을 팝업으로 띄우고, 서버가 verified 를 줄 때까지 조회한다.
 * 팝업이 보내는 메시지는 조회를 앞당기는 신호일 뿐이다. 완료 판단은 서버 조회, 성인 이용 가능 판단은 서버의 adult.eligible 이다.
 * (백엔드 정의서 '다날 휴대전화 본인확인 — 화면 연결 순서')
 */
export default function AdultVerifyModal({
  onEligible,
  onClose,
}: {
  onEligible?: () => void
  onClose: () => void
}) {
  const adult = useSessionStore((s) => s.data?.config.adult)
  const description = useSessionStore(
    (s) => s.data?.config.presets?.find((p) => p.id === 'adult')?.description
  )
  const [waiting, setWaiting] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')
  const attempt = useRef<Attempt | null>(null)
  const timer = useRef<number | null>(null)
  const mounted = useRef(false)
  const startLocked = useRef(false)
  const inFlight = useRef(false)
  const [retry, setRetry] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [accessCheck, setAccessCheck] = useState<
    'checking' | 'ready' | 'error'
  >('checking')
  const accessFlight = useRef(false)
  const accessGeneration = useRef(0)
  const accountSnapshot = useRef<{ identity: number; userId?: string } | null>(
    null
  )
  const eligibleDelivered = useRef(false)
  const onEligibleRef = useLatest(onEligible)
  const onCloseRef = useLatest(onClose)
  const underage = adult?.reason === 'underage'

  const isCurrent = (current: Attempt) => {
    const state = useSessionStore.getState()
    return (
      mounted.current &&
      attempt.current === current &&
      state.identity === current.identity &&
      state.data?.user?.id === current.userId
    )
  }

  const stop = () => {
    if (timer.current !== null) window.clearInterval(timer.current)
    timer.current = null
    if (mounted.current) setWaiting(false)
  }

  const closeOwnLayer = () => {
    onCloseRef.current()
  }

  const grantAccess = (showToast: boolean) => {
    if (eligibleDelivered.current) return
    eligibleDelivered.current = true
    closeOwnLayer()
    if (showToast) toast.show('성인 인증을 마쳤어요')
    onEligibleRef.current?.()
  }

  const checkAccess = async () => {
    if (!mounted.current || accessFlight.current || attempt.current) return
    const state = useSessionStore.getState()
    const identity = state.identity
    const userId = state.data?.user?.id
    accountSnapshot.current = { identity, userId }
    const generation = ++accessGeneration.current
    accessFlight.current = true
    setAccessCheck('checking')
    setError('')
    try {
      const latest = await refreshAdult()
      if (
        !mounted.current ||
        accessGeneration.current !== generation ||
        attempt.current
      )
        return
      const current = useSessionStore.getState()
      if (current.identity !== identity || current.data?.user?.id !== userId)
        return
      if (latest?.eligible) grantAccess(false)
      else setAccessCheck('ready')
    } catch {
      if (
        mounted.current &&
        accessGeneration.current === generation &&
        !attempt.current
      ) {
        setAccessCheck('error')
        setError('인증 상태를 확인하지 못했어요. 다시 확인해 주세요.')
      }
    } finally {
      accessFlight.current = false
      if (
        mounted.current &&
        accessGeneration.current !== generation &&
        !attempt.current
      )
        void checkAccess()
    }
  }

  const finish = (current: Attempt) => {
    if (!isCurrent(current)) return false
    stop()
    attempt.current = null
    current.channel?.close()
    clearIdentityAttempt(current.id)
    current.popup.close()
    if (mounted.current) setConfirming(false)
    return true
  }

  const cancel = async () => {
    const current = attempt.current
    if (!current || !finish(current)) return
    setRetry(false)
    // 확인을 마친 인증은 취소 요청을 보내지 않는다.
    if (!current.verified)
      await api(`/identity/${current.id}/`, undefined, 'DELETE').catch(
        () => null
      )
  }

  const check = async () => {
    const current = attempt.current
    if (!current || !isCurrent(current) || inFlight.current) return
    inFlight.current = true
    try {
      const result = current.verified
        ? { status: 'verified' as const, error_code: undefined }
        : resultOf(await api<unknown>(`/identity/${current.id}/`), current.id)
      const status = result?.status ?? null
      if (!isCurrent(current)) return
      if (status === null) {
        if (Date.now() - current.startedAt > GIVE_UP_MS) {
          if (finish(current))
            setError('인증 결과를 확인하지 못했어요. 다시 시도해 주세요.')
        } else setError('인증 결과를 읽지 못했어요. 다시 확인해 주세요.')
        return
      }
      if (status === 'verified') {
        current.verified = true
        stop()
        setConfirming(true)
        current.popup.close()
        try {
          const next = await refreshSession()
          if (!isCurrent(current)) return
          let eligible = next.config.adult?.eligible === true
          let reason = next.config.adult?.reason
          if (!eligible) {
            const latest = await refreshAdult()
            if (!isCurrent(current)) return
            eligible = latest?.eligible === true
            reason = latest?.reason
          }
          if (!finish(current)) return
          setRetry(false)
          if (eligible) {
            grantAccess(true)
          } else {
            setError(
              reason && REASON_TEXT[reason]
                ? REASON_TEXT[reason]
                : '인증은 끝났지만 성인모드를 열 수 없어요.'
            )
          }
        } catch {
          if (!isCurrent(current)) return
          setConfirming(false)
          setRetry(true)
          setError(
            '인증을 마쳤지만 계정 정보를 불러오지 못했어요. 인증 결과를 다시 확인해 주세요.'
          )
        }
        return
      }
      if (status && STATUS_TEXT[status]) {
        if (finish(current)) {
          const code = result?.error_code
          setError(`${STATUS_TEXT[status]}${code ? ` 오류 코드: ${code}` : ''}`)
        }
        return
      }
      if (Date.now() - current.startedAt > GIVE_UP_MS) {
        if (finish(current))
          setError('인증 결과를 확인하지 못했어요. 다시 시도해 주세요.')
      }
    } catch {
      if (isCurrent(current)) {
        if (Date.now() - current.startedAt > GIVE_UP_MS && !current.verified) {
          if (finish(current))
            setError('인증 결과를 확인하지 못했어요. 다시 시도해 주세요.')
        } else
          setError('인증 결과를 확인하지 못했어요. 잠시 후 다시 확인해 주세요.')
      }
    } finally {
      inFlight.current = false
    }
  }

  const start = async () => {
    if (startLocked.current || attempt.current || accessCheck !== 'ready')
      return
    startLocked.current = true
    setError('')
    setRetry(false)
    setConfirming(false)
    // 클릭 이벤트 안에서 빈 창을 먼저 연다 — 응답을 기다린 뒤 열면 팝업 차단에 걸린다
    const popup = window.open('', 'veluvit-identity', 'width=480,height=720')
    if (!popup) {
      setError(
        '팝업이 차단됐어요. 이 사이트의 팝업을 허용한 뒤 다시 눌러 주세요.'
      )
      startLocked.current = false
      return
    }
    setStarting(true)
    try {
      const user = await ensureAccount()
      if (!mounted.current) {
        popup.close()
        return
      }
      const account = useSessionStore.getState()
      if (account.data?.user?.id !== user.id) {
        popup.close()
        return
      }
      const identity = account.identity
      const mobile = window.matchMedia('(pointer: coarse)').matches
      const res = identityStartSchema.parse(
        await api<unknown>('/identity/', { mobile })
      )
      if (
        !mounted.current ||
        useSessionStore.getState().identity !== identity ||
        useSessionStore.getState().data?.user?.id !== user.id
      ) {
        popup.close()
        return
      }
      const launchUrl = new URL(res.launchUrl, window.location.origin)
      if (launchUrl.protocol !== 'https:' && launchUrl.protocol !== 'http:')
        throw new Error('인증 창 주소를 확인하지 못했어요.')
      const current: Attempt = {
        id: res.verification.id,
        popup,
        startedAt: Date.now(),
        identity,
        userId: user.id,
      }
      attempt.current = current
      rememberIdentityAttempt(current.id, current.userId, popup)
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          current.channel = new BroadcastChannel(
            identityChannelName(current.id)
          )
          current.channel.onmessage = (event) => {
            const parsed = identityMessageSchema.safeParse(event.data)
            if (
              !isCurrent(current) ||
              !parsed.success ||
              parsed.data.id !== current.id
            )
              return
            if (parsed.data.status === 'cancel_requested') {
              void cancel()
              return
            }
            try {
              current.channel?.postMessage({
                type: 'sai.identity.ack',
                id: current.id,
              })
            } catch {
              /* 조회는 계속한다. */
            }
            void check()
          }
        } catch {
          current.channel = undefined
        }
      }
      popup.location.href = launchUrl.href
      setWaiting(true)
      timer.current = window.setInterval(() => void check(), POLL_MS)
    } catch (e) {
      popup.close()
      if (attempt.current?.popup === popup) {
        attempt.current.channel?.close()
        clearIdentityAttempt(attempt.current.id)
        attempt.current = null
      }
      // 시작 요청은 자동으로 다시 보내지 않는다(분당 3회·하루 20회 제한)
      if (mounted.current)
        setError(
          e instanceof ApiError && e.status === 429
            ? '인증 요청이 많아요. 잠시 후 다시 시도해 주세요.'
            : e instanceof Error
              ? e.message
              : '다시 시도해 주세요.'
        )
    } finally {
      startLocked.current = false
      if (mounted.current) setStarting(false)
    }
  }

  // 인증 창의 완료·취소 신호 — 같은 출처·우리가 연 창·같은 시도일 때만 받는다
  useEffect(() => {
    mounted.current = true
    void checkAccess()
    const unsubscribe = useSessionStore.subscribe((state) => {
      const account = accountSnapshot.current
      if (
        account &&
        (state.identity !== account.identity ||
          state.data?.user?.id !== account.userId)
      ) {
        accountSnapshot.current = {
          identity: state.identity,
          userId: state.data?.user?.id,
        }
        accessGeneration.current += 1
        if (!startLocked.current) {
          setAccessCheck('checking')
          if (!accessFlight.current && !attempt.current) void checkAccess()
        }
      }
      const current = attempt.current
      if (
        current &&
        (state.identity !== current.identity ||
          state.data?.user?.id !== current.userId)
      ) {
        stop()
        attempt.current = null
        current.channel?.close()
        clearIdentityAttempt(current.id)
        current.popup.close()
        setConfirming(false)
        setRetry(false)
      }
    })
    const onMessage = (event: MessageEvent) => {
      const current = attempt.current
      if (
        !current ||
        !isCurrent(current) ||
        event.origin !== window.location.origin ||
        event.source !== current.popup
      )
        return
      const parsed = identityMessageSchema.safeParse(event.data)
      if (!parsed.success || parsed.data.id !== current.id) return
      if (parsed.data.status === 'cancel_requested') {
        void cancel()
        return
      }
      try {
        current.popup.postMessage(
          { type: 'sai.identity.ack', id: current.id },
          window.location.origin
        )
      } catch {
        /* 서버 조회는 계속한다. */
      }
      void check()
    }
    window.addEventListener('message', onMessage)
    return () => {
      mounted.current = false
      accessGeneration.current += 1
      unsubscribe()
      window.removeEventListener('message', onMessage)
      if (timer.current !== null) window.clearInterval(timer.current)
      if (attempt.current) clearIdentityAttempt(attempt.current.id)
      attempt.current?.channel?.close()
      attempt.current?.popup.close()
      attempt.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 창이 떠 있는 동안 한 번 붙인다(시도는 ref 로 읽는다)
  }, [])

  const reason =
    adult?.reason &&
    adult.reason !== 'authentication_required' &&
    adult.reason !== 'identity_required'
      ? REASON_TEXT[adult.reason]
      : ''

  if (accessCheck === 'checking' || accessCheck === 'error') {
    return (
      <>
        {accessCheck === 'checking' && (
          <div className="mx-auto mb-4 flex justify-center">
            <Spinner />
          </div>
        )}
        <ModalTitle>
          {accessCheck === 'checking'
            ? '인증 상태 확인 중'
            : '인증 상태를 확인하지 못했어요'}
        </ModalTitle>
        <ModalDesc>
          {accessCheck === 'checking'
            ? '성인모드 이용 가능 여부를 확인하고 있어요.'
            : '연결 상태를 확인한 뒤 다시 시도해 주세요.'}
        </ModalDesc>
        {accessCheck === 'error' && (
          <>
            <p role="alert" className="mt-3 text-center text-c13 text-error">
              {error}
            </p>
            <Button
              variant="accent"
              size="lg"
              shape="pill"
              bold
              className="mt-3 w-full"
              onClick={() => void checkAccess()}
            >
              인증 상태 다시 확인
            </Button>
          </>
        )}
      </>
    )
  }

  return (
    <>
      <div
        aria-hidden="true"
        className="mx-auto mt-2 mb-3 grid size-16 place-items-center rounded-full bg-point/12 text-point"
      >
        <LuShieldCheck className="size-8" />
      </div>
      <ModalTitle>성인 인증이 필요해요</ModalTitle>
      <ModalDesc>
        성인모드는 만 {adult?.minAge || 19}세 이상만 이용할 수 있어요.
        <br />
        휴대폰 본인확인을 한 번 마치면 유효기간 동안 다시 하지 않아도 돼요.
      </ModalDesc>
      {description && (
        <p className="mt-3 rounded-field bg-foreground/[0.05] px-4 py-3 text-center text-c12h leading-[1.6] text-t2">
          {description}
        </p>
      )}
      <p
        role="alert"
        className="mt-3 min-h-5 text-center text-c13 leading-[1.5] text-error"
      >
        {error || reason}
      </p>
      <div className="mt-2 flex flex-col items-center gap-3">
        {underage ? (
          <Button
            variant="secondary"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            onClick={closeOwnLayer}
          >
            닫기
          </Button>
        ) : retry ? (
          <Button
            variant="accent"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            disabled={confirming}
            aria-busy={confirming}
            onClick={() => void check()}
          >
            인증 결과 다시 확인
          </Button>
        ) : (
          <Button
            variant="accent"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            disabled={starting || waiting || confirming}
            aria-busy={starting || waiting || confirming}
            onClick={() => void start()}
          >
            {confirming
              ? '인증 결과 확인 중…'
              : waiting
                ? '인증 창에서 진행해 주세요…'
                : starting
                  ? '인증 창을 여는 중…'
                  : '휴대폰 본인확인'}
          </Button>
        )}
        {waiting && !retry && !confirming && (
          <TextLink onClick={() => void cancel()}>인증 취소</TextLink>
        )}
      </div>
      <Foot className="mt-4">
        확인 결과로 이름과 생년월일만 보관해요. 주민등록번호와 전화번호 원문은
        저장하지 않아요.
      </Foot>
    </>
  )
}
